import { useCallback, useEffect, useRef, useState } from 'react';
import type { DayView, EntryPatch, EntryRow, SectionName } from '../lib/day';
import { advanceTotal, allSigned, dayApi, needsReason, roleOptions, sectionEditable } from '../lib/day';
import { formatHours, formatRs } from '../lib/money';
import { useIsPhone } from '../lib/viewport';
import WorkerCard from '../components/WorkerCard';
import CostCodeSelect from '../components/CostCodeSelect';
import type { CostCode } from '../lib/costcodes';

/**
 * The time sheet. One row per worker, every cell edited in place.
 *
 * Eleven columns:
 *   Worker · On site · Job and pay basis · Cost code · Hrs · OT h · Allowance ·
 *   Reason for allowance · Earned · Advance · Finalise
 *
 * Ten until 11 October 2026, when the owner asked for a cost code on every
 * man's day and accepted the eleventh column. Do not split it into two, and do
 * not add a Sunday column — the Sunday allowance is a second small box inside
 * the allowance cell, so the count never changes between days.
 *
 * Under 700px the same fields become one card per worker (WorkerCard), one
 * expanded at a time. Eleven columns will not fit a 390px screen and shrinking them
 * until they do gives you nothing anybody can tap. Everything above this line —
 * the editing, the debounce, finalise, the signature flow — is shared; only the
 * arrangement differs.
 *
 * Typing never waits for the network: the change lands locally at once and is
 * sent debounced. The server does every division and rounding and its figures
 * are taken as final when they come back.
 */

interface Props {
  view: DayView;
  isOwner: boolean;
  canAdvance: boolean;
  costCodes: CostCode[];
  onChanged: () => void;
  onSignature: (entry: EntryRow) => void;
  onError: (message: string) => void;
}

/** Long enough that a whole number is rarely sent twice, short enough to feel live. */
const DEBOUNCE_MS = 800;

export default function Timesheet({
  view, isOwner, canAdvance, costCodes, onChanged, onSignature, onError,
}: Props) {
  const [rows, setRows] = useState<EntryRow[]>(view.entries);
  const [busy, setBusy] = useState<string | null>(null);
  // Why the last Finalise was refused, shown on that man's own row. It went to
  // the banner above the table until 20 October 2026, and a clerk scrolled to
  // the bottom of fourteen rows saw nothing happen ("i cant finalise 1 person").
  const [refused, setRefused] = useState<{ workerId: string; message: string } | null>(null);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const pending = useRef(new Map<string, EntryPatch>());
  // What the clerk has typed that the server has not yet confirmed, per man.
  // A save's reply, or the day reloading, must never put older text back over
  // it (25 September 2026: "when I am trying to type reason for allowance it
  // delete it self after 2 words").
  const unsaved = useRef(new Map<string, Partial<EntryRow>>());
  // One save at a time per man, each with the row version the last one left —
  // a second save sent while the first was in flight carried the old version,
  // was refused as stale, and the reload wiped what had been typed since.
  const inflight = useRef(new Map<string, Promise<void>>());
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  useEffect(
    () => setRows(view.entries.map((entry) => ({ ...entry, ...unsaved.current.get(entry.workerId) }))),
    [view.entries],
  );

  const editable = sectionEditable(view, 'labour', isOwner);
  const date = view.day.workDate;
  const sunday = view.day.isSunday;
  const phone = useIsPhone();

  // Which card is expanded. "The card being worked on expanded and the rest
  // collapsed" — so exactly one, or none once the last man is done.
  const [openWorker, setOpenWorker] = useState<string | null>(null);

  const send = useCallback(
    async (workerId: string) => {
      const patch = pending.current.get(workerId);
      pending.current.delete(workerId);
      if (!patch) return;

      // Read the row now, not when the timer was set: the previous save may
      // have moved its version on.
      const row = rowsRef.current.find((entry) => entry.workerId === workerId);
      if (!row) return;
      const sent = { ...unsaved.current.get(workerId) };

      try {
        const result = await dayApi.patchEntry(date, workerId, {
          ...patch,
          rowVersion: row.rowVersion,
        });
        // The next save for him may start before React re-renders: give it the
        // new row version now, or it goes out stale and is refused.
        rowsRef.current = rowsRef.current.map((entry) =>
          entry.workerId === workerId ? { ...entry, ...result } : entry,
        );
        // Confirmed: forget what was sent — unless it has been typed over since.
        const still = unsaved.current.get(workerId);
        if (still) {
          for (const key of Object.keys(sent) as (keyof EntryRow)[]) {
            if (still[key] === sent[key]) delete still[key];
          }
          if (Object.keys(still).length === 0) unsaved.current.delete(workerId);
        }
        // The server is the only thing that does the arithmetic, so its numbers
        // replace ours — except what the clerk has typed since this save left.
        setRows((current) =>
          current.map((entry) =>
            entry.workerId === workerId
              ? { ...entry, ...result, ...unsaved.current.get(workerId) }
              : entry,
          ),
        );
        onChanged();
      } catch (err) {
        // Somebody else wrote first — reload rather than clobber. Said on his
        // row: the banner is out of sight, and the reload clears it anyway.
        unsaved.current.delete(workerId);
        setRefused({
          workerId,
          message: err instanceof Error ? err.message : 'That change did not save.',
        });
        onChanged();
      }
    },
    [date, onChanged, onError],
  );

  /** Queued behind any save already on its way for the same man. */
  const flush = useCallback(
    (workerId: string) => {
      timers.current.delete(workerId);
      const before = inflight.current.get(workerId) ?? Promise.resolve();
      const next = before.then(() => send(workerId));
      inflight.current.set(workerId, next);
      void next.finally(() => {
        if (inflight.current.get(workerId) === next) inflight.current.delete(workerId);
      });
      return next;
    },
    [send],
  );

  /** Apply locally now, queue the write. */
  const edit = useCallback(
    (workerId: string, patch: EntryPatch, local: Partial<EntryRow>) => {
      setRefused((current) => (current?.workerId === workerId ? null : current));
      setRows((current) =>
        current.map((entry) => (entry.workerId === workerId ? { ...entry, ...local } : entry)),
      );
      unsaved.current.set(workerId, { ...unsaved.current.get(workerId), ...local });
      pending.current.set(workerId, { ...pending.current.get(workerId), ...patch });

      const existing = timers.current.get(workerId);
      if (existing) clearTimeout(existing);
      timers.current.set(workerId, setTimeout(() => void flush(workerId), DEBOUNCE_MS));
    },
    [flush],
  );

  // Don't leave a half-typed cell unsaved if the screen goes away.
  useEffect(() => {
    const timerMap = timers.current;
    return () => {
      for (const timer of timerMap.values()) clearTimeout(timer);
    };
  }, []);

  async function finalise(entry: EntryRow) {
    // The server refuses this too; saying so here puts the cursor where the
    // clerk has to type, without a round trip.
    const latest = rowsRef.current.find((row) => row.workerId === entry.workerId) ?? entry;
    if (needsReason(latest)) {
      setRefused({
        workerId: entry.workerId,
        message: `Write the reason for the allowance before finalising ${entry.workerName}.`,
      });
      document.querySelector<HTMLInputElement>(`[data-reason="${entry.workerId}"]`)?.focus();
      return;
    }
    setRefused(null);
    setBusy(entry.workerId);
    try {
      // Send anything still queued before locking the row.
      const timer = timers.current.get(entry.workerId);
      if (timer) {
        clearTimeout(timer);
        await flush(entry.workerId);
      }
      const result = await dayApi.finalise(date, entry.workerId);
      onChanged();
      // "If the row has an advance, finalising opens a signature popup."
      if (result.needsSignature) onSignature(entry);
      // On a phone, finishing one man opens the next one still to do — the
      // clerk works down the list and should not have to hunt for his place.
      else if (phone) setOpenWorker(nextToDo(rows, entry.workerId));
    } catch (err) {
      setRefused({
        workerId: entry.workerId,
        message: err instanceof Error ? err.message : 'Could not finalise that row.',
      });
    } finally {
      setBusy(null);
    }
  }

  async function unfinalise(entry: EntryRow) {
    setBusy(entry.workerId);
    try {
      await dayApi.unfinalise(date, entry.workerId);
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not undo that.');
    } finally {
      setBusy(null);
    }
  }

  async function addAdvance(entry: EntryRow, amountRs: number) {
    if (amountRs <= 0) return;
    setBusy(entry.workerId);
    try {
      // Adds one. It never overwrites what is already there.
      await dayApi.addAdvance(date, entry.workerId, amountRs);
      onChanged();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not record that advance.');
    } finally {
      setBusy(null);
    }
  }

  const enteredCount = rows.filter((row) => row.attendance !== null).length;
  const finalCount = rows.filter((row) => row.finalised).length;
  const labourTotal = rows.reduce((sum, row) => sum + row.earnedRs, 0);
  const advancesTotal = rows.reduce((sum, row) => sum + advanceTotal(row), 0);

  if (rows.length === 0) {
    return <div className="empty">No employees yet. The owner adds them in Settings.</div>;
  }

  // ------------------------------------------------------------- phone
  if (phone) {
    return (
      <>
        <div className="wcards">
          {rows.map((entry) => (
            <WorkerCard
              key={entry.workerId}
              entry={entry}
              sunday={sunday}
              editable={editable}
              canAdvance={canAdvance}
              costCodes={costCodes}
              busy={busy === entry.workerId}
              refused={refused?.workerId === entry.workerId ? refused.message : null}
              open={openWorker === entry.workerId}
              onOpen={() =>
                setOpenWorker((current) => (current === entry.workerId ? null : entry.workerId))
              }
              onEdit={edit}
              onFinalise={() => void finalise(entry)}
              onUnfinalise={() => void unfinalise(entry)}
              onAddAdvance={(amount) => void addAdvance(entry, amount)}
              onSignature={() => onSignature(entry)}
            />
          ))}
        </div>

        {/* The table's tfoot, which a stack of cards has nowhere to put. */}
        <div className="wtotals">
          <div>
            {enteredCount} of {rows.length} entered · {finalCount} finalised
          </div>
          <div className="wtotals-money">
            <span>
              <span className="tile-label">Labour</span>
              {formatRs(labourTotal)}
            </span>
            <span>
              <span className="tile-label">Advances</span>
              {formatRs(advancesTotal)}
            </span>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="scroll">
      <table className="tt">
        <thead>
          <tr>
            <th>Employee</th>
            <th>On site</th>
            <th>Job and pay basis</th>
            <th>Cost code</th>
            <th className="num">Hrs</th>
            <th className="num">OT h</th>
            <th className="num">Allowance{sunday ? ' + Sunday' : ''}</th>
            <th>Reason for allowance</th>
            <th className="num">Earned</th>
            <th className="num">Advance</th>
            <th style={{ textAlign: 'right' }}>Finalise</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((entry) => (
            <Row
              key={entry.workerId}
              entry={entry}
              sunday={sunday}
              editable={editable}
              canAdvance={canAdvance}
              costCodes={costCodes}
              busy={busy === entry.workerId}
              refused={refused?.workerId === entry.workerId ? refused.message : null}
              onEdit={edit}
              onFinalise={() => void finalise(entry)}
              onUnfinalise={() => void unfinalise(entry)}
              onAddAdvance={(amount) => void addAdvance(entry, amount)}
              onSignature={() => onSignature(entry)}
            />
          ))}
        </tbody>
        <tfoot>
          <tr>
            {/* Everything before Earned: the prototype's colspan 6 was one short
                of its own head even before the Cost code column made it 8. */}
            <th colSpan={8}>
              {enteredCount} of {rows.length} entered · {finalCount} finalised
            </th>
            <th className="num">{formatRs(labourTotal)}</th>
            <th className="num">{formatRs(advancesTotal)}</th>
            <th />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * The next man after this one who still needs finalising, wrapping to the top
 * so a clerk who started in the middle still reaches everybody. Null when they
 * are all done.
 */
function nextToDo(rows: EntryRow[], afterWorkerId: string): string | null {
  const start = rows.findIndex((row) => row.workerId === afterWorkerId);
  for (let step = 1; step <= rows.length; step += 1) {
    const candidate = rows[(start + step) % rows.length];
    if (candidate && candidate.workerId !== afterWorkerId && !candidate.finalised) {
      return candidate.workerId;
    }
  }
  return null;
}

// ---------------------------------------------------------------- one row

interface RowProps {
  entry: EntryRow;
  sunday: boolean;
  editable: boolean;
  canAdvance: boolean;
  costCodes: CostCode[];
  busy: boolean;
  /** Why Finalise was just refused for this man, if it was. */
  refused: string | null;
  onEdit: (workerId: string, patch: EntryPatch, local: Partial<EntryRow>) => void;
  onFinalise: () => void;
  onUnfinalise: () => void;
  onAddAdvance: (amountRs: number) => void;
  onSignature: () => void;
}

function Row({
  entry, sunday, editable, canAdvance, costCodes, busy, refused,
  onEdit, onFinalise, onUnfinalise, onAddAdvance, onSignature,
}: RowProps) {
  const [advanceDraft, setAdvanceDraft] = useState('');

  // "The rest of the row is locked until it is set."
  // Approved is locked for the clerk whatever finalised says (rule 15).
  const approved = entry.status === 'approved';
  const active = entry.attendance === 'worked' && !entry.finalised && !approved;
  const byDay = entry.payBasis === 'day';
  const locked = !editable || entry.finalised || approved;
  const reasonMissing = needsReason(entry);
  const advances = advanceTotal(entry);

  /** The job select lists each role twice — "day rate" and "trip pay". */
  const jobValue = `${entry.jobRoleId ?? ''}:${entry.payBasis}`;

  return (
    <tr className={entry.attendance === null ? 'dim' : undefined}>
      <td className="nm">
        {entry.workerName}
        {entry.rejected && (
          <div className="why" style={{ color: 'var(--text-danger)' }}>
            sent back — fix this
          </div>
        )}
      </td>

      <td>
        <select
          className="w"
          value={entry.attendance ?? ''}
          disabled={locked}
          onChange={(e) => {
            const value = e.target.value === '' ? null : (e.target.value as 'worked' | 'absent');
            onEdit(
              entry.workerId,
              { attendance: value },
              value === 'absent'
                ? {
                    attendance: value, hoursTenths: 0, otTenths: 0, allowanceRs: 0,
                    sundayAllowanceRs: 0, salaryRs: 0, otRs: 0, earnedRs: 0, baseEarnedRs: 0,
                  }
                : { attendance: value },
            );
          }}
        >
          <option value="">— pick —</option>
          <option value="worked">Worked</option>
          <option value="absent">Absent</option>
        </select>
      </td>

      <td>
        <select
          className="j"
          value={jobValue}
          disabled={!active || locked}
          onChange={(e) => {
            const [roleId, basis] = e.target.value.split(':');
            const payBasis = basis === 'trip' ? 'trip' : 'day';
            onEdit(
              entry.workerId,
              roleId ? { jobRoleId: roleId, payBasis } : { payBasis },
              { payBasis, jobRoleId: roleId ?? null },
            );
          }}
        >
          {roleOptions(entry).map((role) => (
            <optgroup key={role.id} label={role.name}>
              <option value={`${role.id}:day`}>{role.name} — day rate</option>
              <option value={`${role.id}:trip`}>{role.name} — trip pay</option>
            </optgroup>
          ))}
          {roleOptions(entry).length === 0 && <option value=":day">No job role set</option>}
        </select>
      </td>

      <td>
        <CostCodeSelect
          className="cc"
          compact
          codes={costCodes}
          value={entry.costCodeId}
          usual={entry.usualCostCodeId}
          disabled={!active || locked}
          onChange={(costCodeId) => onEdit(entry.workerId, { costCodeId }, { costCodeId })}
        />
      </td>

      <td className="num">
        <input
          className="n"
          type="number"
          step="0.5"
          min="0"
          max="24"
          value={formatHours(entry.hoursTenths)}
          disabled={!active || !byDay || locked}
          onChange={(e) => {
            const tenths = Math.round((Number(e.target.value) || 0) * 10);
            onEdit(entry.workerId, { hoursTenths: tenths }, { hoursTenths: tenths });
          }}
        />
      </td>

      <td className="num">
        <input
          className="n"
          type="number"
          step="0.5"
          min="0"
          value={formatHours(entry.otTenths)}
          disabled={!active || !byDay || locked}
          onChange={(e) => {
            const tenths = Math.round((Number(e.target.value) || 0) * 10);
            onEdit(entry.workerId, { otTenths: tenths }, { otTenths: tenths });
          }}
        />
      </td>

      {/* The Sunday box lives inside this cell, so the column count is the same
          on a Sunday as on any other day. */}
      <td className="num">
        <input
          className="n"
          type="number"
          min="0"
          value={entry.allowanceRs || ''}
          disabled={!active || locked}
          onChange={(e) => {
            const amount = Math.max(0, Math.trunc(Number(e.target.value) || 0));
            onEdit(entry.workerId, { allowanceRs: amount }, { allowanceRs: amount });
          }}
        />
        {sunday && (
          <div style={{ marginTop: 4 }}>
            <input
              className="n"
              type="number"
              min="0"
              title="Sunday allowance"
              value={entry.sundayAllowanceRs || ''}
              disabled={!active || locked}
              onChange={(e) => {
                const amount = Math.max(0, Math.trunc(Number(e.target.value) || 0));
                onEdit(
                  entry.workerId,
                  { sundayAllowanceRs: amount },
                  { sundayAllowanceRs: amount },
                );
              }}
            />
            <div className="why">Sunday</div>
          </div>
        )}
      </td>

      <td>
        <input
          className={`r${reasonMissing ? ' need' : ''}`}
          type="text"
          data-reason={entry.workerId}
          value={entry.allowanceReason ?? ''}
          placeholder={entry.allowanceRs > 0 ? 'required' : 'why was it paid'}
          disabled={!active || locked}
          onChange={(e) =>
            onEdit(
              entry.workerId,
              { allowanceReason: e.target.value },
              { allowanceReason: e.target.value },
            )
          }
        />
      </td>

      <td className="num nm">
        {entry.attendance === 'worked' ? formatRs(entry.earnedRs) : '—'}
        {entry.queued && <div className="why">queued</div>}
      </td>

      {/* Adds an advance; it never replaces one. */}
      <td className="num">
        {advances > 0 && <div className="nm">{formatRs(advances)}</div>}
        <input
          className="n"
          type="number"
          min="0"
          placeholder="add"
          value={advanceDraft}
          disabled={!active || !canAdvance || busy}
          onChange={(e) => setAdvanceDraft(e.target.value)}
          onBlur={() => {
            const amount = Math.trunc(Number(advanceDraft) || 0);
            if (amount > 0) onAddAdvance(amount);
            setAdvanceDraft('');
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
      </td>

      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
        {entry.attendance === null ? (
          <span className="sub">—</span>
        ) : approved ? (
          <span className="pill p-ok">Approved</span>
        ) : entry.finalised ? (
          <>
            <span className="pill p-ok">Final</span>{' '}
            {editable && (
              <button
                type="button"
                className="btn sm"
                style={{ minWidth: 0 }}
                disabled={busy}
                onClick={onUnfinalise}
              >
                Undo
              </button>
            )}
          </>
        ) : editable ? (
          <button
            type="button"
            className="btn sm pri"
            style={{ minWidth: 0 }}
            disabled={busy}
            onClick={onFinalise}
          >
            Finalise
          </button>
        ) : (
          '—'
        )}

        {refused && (
          <div
            className="why"
            style={{ color: 'var(--text-danger)', whiteSpace: 'normal', maxWidth: 180, marginLeft: 'auto' }}
          >
            {refused}
          </div>
        )}

        {advances > 0 && (
          <button
            type="button"
            className="why"
            onClick={onSignature}
            style={{
              cursor: 'pointer',
              textDecoration: 'underline',
              background: 'none',
              border: 0,
              padding: 0,
              display: 'block',
              marginLeft: 'auto',
            }}
          >
            {allSigned(entry) ? (
              'signature held'
            ) : (
              <span style={{ color: 'var(--text-danger)' }}>no signature</span>
            )}
          </button>
        )}
      </td>
    </tr>
  );
}

export type { SectionName };
